import { useCallback, useMemo } from 'react';
import type { FilterField, FilterLine } from '../types';
import { hasIncompleteFilter } from '../vyTableFilterUtils';
import { useFilterState } from './use-filter-state';

export const useTableFilter = (fields: FilterField[], defaultFilters: FilterLine[] = []) => {
  const { filterLines, addFilter, deleteFilter, updateFilter, resetFilters } = useFilterState(defaultFilters);

  const getAvailableFields = useCallback(
    (currentFilterId: string) => {
      const usedFieldIds = (filterLines ?? [])
        .filter((line) => line.id !== currentFilterId && line.field !== '')
        .map((line) => line.field);

      return fields.filter((field) => !usedFieldIds.includes(field.id));
    },
    [fields, filterLines],
  );

  const getFieldOperators = useCallback(
    (fieldId: string) => {
      if (!fieldId) return [];

      const field = fields.find((f) => f.id === fieldId);
      return field?.operators ?? [];
    },
    [fields],
  );

  const getFieldInputType = useCallback(
    (fieldId: string) => {
      if (!fieldId) return 'text';

      const field = fields.find((f) => f.id === fieldId);
      return field?.inputType ?? 'text';
    },
    [fields],
  );

  const getFieldOptions = useCallback(
    (fieldId: string) => {
      if (!fieldId) return [];

      const field = fields.find((f) => f.id === fieldId);
      return field?.options ?? [];
    },
    [fields],
  );

  const hasIncompleteFilters = useMemo(() => {
    return (filterLines ?? []).some(hasIncompleteFilter);
  }, [filterLines]);

  const canAddFilter = useMemo(() => {
    const validFilters = (filterLines ?? []).filter((filter) => !hasIncompleteFilter(filter));
    return !hasIncompleteFilters && validFilters.length < fields.length;
  }, [hasIncompleteFilters, filterLines, fields.length]);

  return {
    filterLines,
    addFilter,
    deleteFilter,
    updateFilter,
    resetFilters,
    getAvailableFields,
    getFieldOperators,
    getFieldInputType,
    getFieldOptions,
    canAddFilter,
    hasIncompleteFilters,
  };
};
